import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import { isMimeType, mediaFileReader } from '@lexical/utils';
import { $getRoot, COMMAND_PRIORITY_LOW } from 'lexical';
import { useEffect } from 'react';

import { INSERT_IMAGE_COMMAND } from './ImagePlugin';
import useFileQueries from '@/dashboard/hooks/queries/useFileQueries';
import getUrlFromCdn, { generateSrcSetUrls } from '@/dashboard/services/getUrlFromCdn';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import logException from '@/dashboard/services/logException';
import shortUuid from '../../shortUuid';
import preloadImages from '../../preloadImages';

const ACCEPTABLE_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

export default function DragDropPastePlugin({ setIsLoading }) {
  const [editor] = useLexicalComposerContext();
  const { uploadFile } = useFileQueries();
  const { sellerId } = useAuth();

  /**
   * We use this isLoadingCallback function because without it react loses track of the reactivity of setIsLoading
   */
  function isLoadingCallback(state) {
    setIsLoading(state);
  }

  useEffect(() => {
    editor.registerCommand(
      DRAG_DROP_PASTE,
      files => {
        (async () => {
          const filesResult = await mediaFileReader(
            files,
            [ACCEPTABLE_IMAGE_TYPES].flatMap(x => x),
          );

          isLoadingCallback(true);
          for (const { file, result } of filesResult) {
            if (!isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
              isLoadingCallback(false);
              continue;
            }
            const reader = new FileReader();

            reader.onload = event => {
              const img = new Image();
              img.src = event.target.result;

              img.onload = async () => {
                // Now the image object has the file data loaded
                const width = parseInt(img.width > 448 ? 448 : img.width);

                // Calculate the aspect ratio and the resulting height
                const aspectRatio = img.width / img.height;
                const newHeight = parseInt(width / aspectRatio);

                // Create a canvas element to resize the image
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = newHeight;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, newHeight);

                const tmpId = shortUuid();
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  payload: {
                    src: result,
                    isLoading: true,
                    width: width,
                    height: newHeight,
                    id: tmpId,
                  },
                });
                try {
                  const { data } = await uploadFile({
                    file,
                    bucket: 'images',
                    sellerId,
                  });

                  const imageUrl = getUrlFromCdn(data.location, { width, height: newHeight });
                  const urls = generateSrcSetUrls({ url: imageUrl, width, height: newHeight });
                  preloadImages({ urls: Object.values(urls) }).catch(err => console.error(err));

                  editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                    payload: {
                      src: imageUrl,
                    },
                    nodeId: tmpId,
                  });
                } catch (err) {
                  logException(err);
                  editor.update(() => {
                    $getRoot()
                      .getChildren()
                      .forEach(node => {
                        if (node.__id === tmpId) {
                          node.remove();
                        }
                      });
                  });
                }
                isLoadingCallback(false);
              };
            };

            reader.readAsDataURL(file);
          }
        })();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);
  return null;
}
